import React, { useEffect, useState } from 'react';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { FileText, Download, Mail, Trash2, Plus, Loader2 } from 'lucide-react';

type ReportListItem = { id: string; title: string; periodStart: string | null; periodEnd: string | null; scope: any; source: string; model: string | null; createdAt: string };
type Report = ReportListItem & { markdown: string };
type Subscription = { id: string; name: string; cadence: string; recipients: string[]; scope: any; enabled: boolean; lastSentAt: string | null };

const fmt = (d?: string | null) => (d ? new Date(d).toLocaleDateString('en-CA', { year: 'numeric', month: 'short', day: 'numeric' }) : '—');

export default function Reports() {
  const [provinces, setProvinces] = useState<string[]>([]);
  const [reports, setReports] = useState<ReportListItem[]>([]);
  const [active, setActive] = useState<Report | null>(null);
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [subs, setSubs] = useState<Subscription[]>([]);

  // generate form
  const [province, setProvince] = useState('');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const [focus, setFocus] = useState('');
  const [model, setModel] = useState('claude-sonnet-4-6');
  const [generating, setGenerating] = useState(false);

  const loadReports = () => axios.get('/api/reports').then(r => setReports(r.data)).catch(console.error);
  const loadSubs = () => axios.get('/api/reports/subscriptions/all').then(r => setSubs(r.data)).catch(console.error);

  useEffect(() => {
    axios.get('/api/library/provinces').then(r => setProvinces(r.data)).catch(console.error);
    axios.get('/api/reports/email-status').then(r => setEmailEnabled(r.data.enabled)).catch(console.error);
    loadReports();
    loadSubs();
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await axios.post('/api/reports/generate', { province, start, end, focus, model });
      setActive(r.data);
      loadReports();
    } catch (e) { console.error(e); alert('Failed to generate report'); }
    finally { setGenerating(false); }
  };

  const open = (id: string) => axios.get(`/api/reports/${id}`).then(r => setActive(r.data)).catch(console.error);
  const remove = (id: string) => axios.delete(`/api/reports/${id}`).then(() => { loadReports(); setActive(a => a?.id === id ? null : a); }).catch(console.error);

  const emailReport = async (id: string) => {
    const to = prompt('Send to (comma-separated emails):');
    if (!to) return;
    try {
      const r = await axios.post(`/api/reports/${id}/send`, { recipients: to.split(',').map(s => s.trim()).filter(Boolean) });
      alert(r.data.sent ? 'Email sent.' : `Not sent: ${r.data.reason}`);
    } catch (e) { alert('Failed to send'); }
  };

  // Print just the report. A print stylesheet (below) hides app chrome.
  const downloadPdf = () => window.print();

  return (
    <div className="bg-transparent min-h-full p-8">
      <style>{`@media print { body * { visibility: hidden; } #report-print, #report-print * { visibility: visible; } #report-print { position: absolute; left: 0; top: 0; width: 100%; padding: 24px; color: #000; } }`}</style>

      <div className="mb-6 no-print">
        <h1 className="text-[22px] font-semibold text-[color:var(--text-primary)] m-0 mb-1">Reports</h1>
        <p className="text-[13px] text-[color:var(--text-muted)] m-0">Generate executive briefs on NAM trends, export them, and schedule email digests.</p>
      </div>

      <div className="grid gap-6 [grid-template-columns:minmax(0,1fr)] lg:[grid-template-columns:minmax(300px,360px)_minmax(0,1fr)]">
        {/* Left: generate + history + digests */}
        <div className="no-print">
          {/* Generate */}
          <div className="bg-[var(--glass-bg)] backdrop-blur-[12px] border border-[var(--glass-border)] rounded-[16px] p-5 mb-6">
            <div className="text-[14px] font-semibold text-[color:var(--text-primary)] mb-3">Generate a report</div>
            <div className="flex flex-col gap-[10px]">
              <select value={province} onChange={e => setProvince(e.target.value)} className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[7px] text-[13px] text-[color:var(--text-primary)]">
                <option value="">All provinces</option>
                {provinces.map(p => <option key={p} value={p}>{p}</option>)}
              </select>
              <div className="flex gap-2">
                <label className="flex-1 text-[11px] text-[color:var(--text-muted)]">From<input type="date" value={start} onChange={e => setStart(e.target.value)} className="w-full mt-1 bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[6px] text-[12px] text-[color:var(--text-primary)]" /></label>
                <label className="flex-1 text-[11px] text-[color:var(--text-muted)]">To<input type="date" value={end} onChange={e => setEnd(e.target.value)} className="w-full mt-1 bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[6px] text-[12px] text-[color:var(--text-primary)]" /></label>
              </div>
              <input value={focus} onChange={e => setFocus(e.target.value)} placeholder="Focus (optional, e.g. flood / valuation)" className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[7px] text-[13px] text-[color:var(--text-primary)] placeholder:text-[color:var(--text-muted)]" />
              <select value={model} onChange={e => setModel(e.target.value)} className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[7px] text-[13px] text-[color:var(--text-primary)]">
                <option value="claude-sonnet-4-6">Standard (Sonnet)</option>
                <option value="deep">Deep (Opus)</option>
              </select>
              <button onClick={generate} disabled={generating} className="inline-flex items-center justify-center gap-2 text-[13px] px-3 py-2 rounded-[10px] bg-[var(--accent)] text-white hover:opacity-90 disabled:opacity-60">
                {generating ? <><Loader2 size={15} className="animate-spin" /> Generating…</> : <><FileText size={15} /> Generate</>}
              </button>
            </div>
          </div>

          {/* History */}
          <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-4 mb-6">
            <div className="text-[14px] font-semibold text-[color:var(--text-primary)] mb-2">Past reports</div>
            {reports.length === 0 && <p className="text-[12px] text-[color:var(--text-muted)]">None yet.</p>}
            <div className="flex flex-col gap-1 max-h-[280px] overflow-y-auto">
              {reports.map(r => (
                <div key={r.id} className={`flex items-center gap-2 rounded-[8px] px-2 py-[6px] cursor-pointer ${active?.id === r.id ? 'bg-[var(--surface)]' : 'hover:bg-[var(--surface)]'}`}>
                  <button onClick={() => open(r.id)} className="text-left flex-1 min-w-0">
                    <div className="text-[12px] text-[color:var(--text-primary)] truncate">{r.title}</div>
                    <div className="text-[11px] text-[color:var(--text-muted)]">{fmt(r.createdAt)}{r.source === 'digest' ? ' · digest' : ''}</div>
                  </button>
                  <button onClick={() => remove(r.id)} title="Delete" className="text-[color:var(--text-muted)] hover:text-[#f472b6] shrink-0"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Digests */}
          <DigestManager subs={subs} provinces={provinces} emailEnabled={emailEnabled} reload={loadSubs} />
        </div>

        {/* Right: active report */}
        <div className="min-w-0">
          {active ? (
            <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-6">
              <div className="flex items-center gap-2 mb-4 no-print">
                <div className="text-[13px] text-[color:var(--text-muted)] flex-1">{active.model} · {active.source}</div>
                <button onClick={downloadPdf} className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-[8px] border border-[var(--border)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"><Download size={13} /> PDF</button>
                {emailEnabled && <button onClick={() => emailReport(active.id)} className="inline-flex items-center gap-1 text-[12px] px-2 py-1 rounded-[8px] border border-[var(--border)] text-[color:var(--text-secondary)] hover:text-[color:var(--text-primary)]"><Mail size={13} /> Email</button>}
              </div>
              <div id="report-print" className="prose-report text-[14px] leading-[1.65] text-[color:var(--text-primary)] [&_h1]:text-[20px] [&_h1]:font-semibold [&_h2]:text-[16px] [&_h2]:font-semibold [&_h2]:mt-5 [&_h2]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_li]:mb-1 [&_p]:mb-3 [&_a]:text-[color:var(--accent-blue)] [&_hr]:my-4 [&_hr]:border-[var(--border)]">
                <ReactMarkdown remarkPlugins={[remarkGfm]}>{active.markdown}</ReactMarkdown>
              </div>
            </div>
          ) : (
            <div className="h-full min-h-[300px] flex items-center justify-center text-[13px] text-[color:var(--text-muted)] border border-dashed border-[var(--border)] rounded-[16px] no-print">
              Generate a report or pick one from the list.
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

function DigestManager({ subs, provinces, emailEnabled, reload }: { subs: Subscription[]; provinces: string[]; emailEnabled: boolean; reload: () => void }) {
  const [adding, setAdding] = useState(false);
  const [name, setName] = useState('');
  const [cadence, setCadence] = useState('weekly');
  const [recipients, setRecipients] = useState('');
  const [province, setProvince] = useState('');

  const create = async () => {
    if (!name.trim()) return;
    await axios.post('/api/reports/subscriptions', {
      name, cadence,
      recipients: recipients.split(',').map(s => s.trim()).filter(Boolean),
      scope: { province: province || 'All provinces' },
    }).catch(console.error);
    setName(''); setRecipients(''); setProvince(''); setAdding(false); reload();
  };
  const toggle = (s: Subscription) => axios.patch(`/api/reports/subscriptions/${s.id}`, { enabled: !s.enabled }).then(reload).catch(console.error);
  const del = (id: string) => axios.delete(`/api/reports/subscriptions/${id}`).then(reload).catch(console.error);

  return (
    <div className="bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-[16px] p-4">
      <div className="flex items-center justify-between mb-2">
        <div className="text-[14px] font-semibold text-[color:var(--text-primary)]">Email digests</div>
        <button onClick={() => setAdding(a => !a)} className="text-[color:var(--accent-blue)] inline-flex items-center gap-1 text-[12px]"><Plus size={13} /> New</button>
      </div>
      {!emailEnabled && <div className="text-[11px] text-[#f59e0b] mb-2">Email isn’t configured yet (set Postmark credentials). Digests will generate reports but can’t send until then.</div>}

      {adding && (
        <div className="flex flex-col gap-[8px] mb-3 pb-3 border-b border-[var(--border)]">
          <input value={name} onChange={e => setName(e.target.value)} placeholder="Name (e.g. Weekly NB brief)" className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[6px] text-[12px] text-[color:var(--text-primary)]" />
          <div className="flex gap-2">
            <select value={cadence} onChange={e => setCadence(e.target.value)} className="flex-1 bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[6px] text-[12px] text-[color:var(--text-primary)]">
              <option value="weekly">Weekly</option>
              <option value="monthly">Monthly</option>
            </select>
            <select value={province} onChange={e => setProvince(e.target.value)} className="flex-1 bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[6px] text-[12px] text-[color:var(--text-primary)]">
              <option value="">All provinces</option>
              {provinces.map(p => <option key={p} value={p}>{p}</option>)}
            </select>
          </div>
          <input value={recipients} onChange={e => setRecipients(e.target.value)} placeholder="Recipients (comma-separated emails)" className="bg-[var(--surface)] border border-[var(--border)] rounded-[8px] px-2 py-[6px] text-[12px] text-[color:var(--text-primary)]" />
          <button onClick={create} className="text-[12px] px-3 py-[6px] rounded-[8px] bg-[var(--accent)] text-white">Add subscription</button>
        </div>
      )}

      {subs.length === 0 && !adding && <p className="text-[12px] text-[color:var(--text-muted)]">No digests.</p>}
      <div className="flex flex-col gap-2">
        {subs.map(s => (
          <div key={s.id} className="flex items-center gap-2 text-[12px]">
            <button onClick={() => toggle(s)} title={s.enabled ? 'Enabled' : 'Disabled'} className={`w-2.5 h-2.5 rounded-full shrink-0 ${s.enabled ? 'bg-[var(--accent)]' : 'bg-[var(--text-muted)]'}`} />
            <div className="flex-1 min-w-0">
              <div className="text-[color:var(--text-primary)] truncate">{s.name}</div>
              <div className="text-[11px] text-[color:var(--text-muted)] truncate">{s.cadence} · {s.scope?.province || 'All'} · {s.recipients.length} recipient{s.recipients.length === 1 ? '' : 's'}{s.lastSentAt ? ` · last ${fmt(s.lastSentAt)}` : ''}</div>
            </div>
            <button onClick={() => del(s.id)} title="Delete" className="text-[color:var(--text-muted)] hover:text-[#f472b6] shrink-0"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>
    </div>
  );
}
